import type { Elevator, GameState, Passenger } from '../types.js';
import { valueOfDelivery } from './service.js';
import { alightFloor } from './transfer.js';

/**
 * Power-ups (ADR-0055).
 *
 * The owner asked for power-ups, chose four, and chose how they are earned: a
 * meter that fills in the shift. People home in a row fill it; anyone lost
 * empties it. A full meter is a charge, and a charge is spent on whichever of
 * the four fits the moment. Nothing is bought and nothing carries over: an item
 * economy would be a second currency (BACKLOG, one-use rewards).
 *
 * Each power eases one rule, in the sim, through a command, so a run that uses
 * one replays exactly. A run that never does is untouched: the meter only
 * counts, and nothing it holds is hashed.
 */

export type PowerName = 'squeeze' | 'hold' | 'announce' | 'stairs';

export const POWER_NAMES: readonly PowerName[] = ['squeeze', 'hold', 'announce', 'stairs'];

export const POWER = {
  /** People home in a row, with nobody lost, for a charge. */
  meterFull: 6,
  /** Charges held at once: one, spent before the next is earned. */
  maxCharges: 1,
  /** Squeeze in: seats over the cabin's own, for one stop. */
  squeezeSeats: 2,
  /** Hold the departure: how much later the soonest one leaves. */
  holdMs: 15_000,
  /** Announcement: how much longer everyone waiting will wait. */
  announceMs: 8_000,
  /** The stairs: how far someone will walk. */
  stairsFloors: 2,
  /** The stairs: the share of a delivery's points walking earns. */
  stairsShare: 0.5,
} as const;

/** Who leaves at a set time (ADR-0051): a train's commuters and the hotel's departures. */
const DEPARTING = new Set(['commuter', 'shuttleGuest', 'breakfastGuest', 'newlywed']);

/** The meter and the charge, and what was last used, for the view's word on it. */
export interface PowerState {
  /** Whether this shift has power-ups at all: not a puzzle, which counts trips. */
  readonly allowed: boolean;
  meter: number;
  charges: number;
  /** The delivered and lost counts the meter last saw. */
  seenDelivered: number;
  seenLost: number;
  /** The power used last, and when, or null. */
  last: { readonly name: PowerName; readonly atMs: number } | null;
}

export function newPowerState(allowed: boolean): PowerState {
  return { allowed, meter: 0, charges: 0, seenDelivered: 0, seenLost: 0, last: null };
}

/** The cabin's seats now: its own, and two more while squeezed in. */
export function capacityOf(state: GameState, car: Elevator = state.elevator): number {
  return state.spec.capacity + (car.squeeze > 0 ? POWER.squeezeSeats : 0);
}

/**
 * Fill the meter from this tick's deliveries; a loss empties it. Once a tick.
 * While a charge is held the meter waits: the next is earned after it is spent.
 */
export function chargePowers(state: GameState): void {
  const power = state.power;
  if (!power.allowed) return;
  const lost = state.score.abandoned + state.score.couriersLate;
  const delivered = state.score.delivered;
  if (lost > power.seenLost) {
    power.meter = 0;
  } else if (delivered > power.seenDelivered && power.charges < POWER.maxCharges) {
    power.meter = Math.min(POWER.meterFull, power.meter + delivered - power.seenDelivered);
  }
  power.seenLost = lost;
  power.seenDelivered = delivered;
  if (power.meter >= POWER.meterFull && power.charges < POWER.maxCharges) {
    power.charges += 1;
    power.meter = 0;
  }
}

/** The soonest departure still to leave, by when it leaves, or null. */
function soonestDeparture(state: GameState): number | null {
  let soonest: number | null = null;
  for (const p of state.passengers) {
    if (p.deadlineMs === null || !DEPARTING.has(p.archetype)) continue;
    if (p.state === 'delivered' || p.state === 'abandoned') continue;
    if (p.deadlineMs <= state.elapsedMs) continue;
    if (soonest === null || p.deadlineMs < soonest) soonest = p.deadlineMs;
  }
  return soonest;
}

/** Someone who may take the stairs: alone, nothing to carry, nowhere else to be, close to home. */
export function canTakeStairs(state: GameState, p: Passenger): boolean {
  if (p.state !== 'waiting') return false;
  if (p.size !== 1 || p.groupId !== null || p.critical || p.alone) return false;
  if (p.errands.length > 0 || p.deadlineMs !== null) return false;
  const out = alightFloor(state, p);
  if (out !== p.destination || out === p.standingFloor) return false;
  return Math.abs(out - p.standingFloor) <= POWER.stairsFloors;
}

/** Whether a power would do anything now: what the view dims, and what a command checks. */
export function powerApplies(state: GameState, name: PowerName, car: Elevator): boolean {
  if (!state.power.allowed || state.power.charges < 1) return false;
  switch (name) {
    case 'squeeze':
      return car.squeeze === 0;
    case 'hold':
      return soonestDeparture(state) !== null;
    case 'announce':
      return state.passengers.some((p) => p.state === 'waiting' && p.patienceMs !== null);
    case 'stairs':
      return state.passengers.some((p) => canTakeStairs(state, p));
  }
}

/** A trait a power moves: the one place a passenger's clock changes in a run. */
type Clocks = { patienceMs: number | null; deadlineMs: number | null };

/** Squeeze in: at an open door it counts at once; on the way, at the next stop. */
function squeeze(car: Elevator): void {
  const atDoor =
    car.phase === 'servicing' && (car.doorPhase === 'open' || car.doorPhase === 'opening');
  car.squeeze = atDoor ? 2 : 1;
}

/** Hold the departure: everyone on the soonest one has longer to catch it. */
function hold(state: GameState): void {
  const leaving = soonestDeparture(state);
  for (const p of state.passengers) {
    if (p.deadlineMs !== leaving || !DEPARTING.has(p.archetype)) continue;
    if (p.state === 'delivered' || p.state === 'abandoned') continue;
    (p as Clocks).deadlineMs = (p.deadlineMs ?? 0) + POWER.holdMs;
  }
}

/** The announcement: everyone waiting with a limit waits longer. */
function announce(state: GameState): void {
  for (const p of state.passengers) {
    if (p.state !== 'waiting' || p.patienceMs === null) continue;
    (p as Clocks).patienceMs = p.patienceMs + POWER.announceMs;
  }
}

/** The stairs: someone walks home, for part of the points. False when they may not. */
function walk(state: GameState, passengerId: string | undefined): boolean {
  const p = state.passengers.find((x) => x.id === passengerId);
  if (!p || !canTakeStairs(state, p)) return false;
  p.state = 'delivered';
  p.deliveredMs = state.elapsedMs;
  state.score.delivered += 1;
  state.score.service += Math.round(valueOfDelivery(p, state.elapsedMs).total * POWER.stairsShare);
  // Walking home is not a delivery the meter counts.
  state.power.seenDelivered += 1;
  return true;
}

/**
 * Spend the charge. `car` is the car a squeeze is for; `passengerId` who takes
 * the stairs. A power that would do nothing spends nothing.
 */
export function usePower(
  state: GameState,
  name: PowerName,
  car: Elevator,
  passengerId?: string,
): void {
  if (!powerApplies(state, name, car)) return;
  if (name === 'squeeze') squeeze(car);
  else if (name === 'hold') hold(state);
  else if (name === 'announce') announce(state);
  else if (!walk(state, passengerId)) return;
  state.power.charges -= 1;
  state.power.last = { name, atMs: state.elapsedMs };
}
